<script setup lang="ts">
import { Hooper, Slide, Navigation, Pagination } from '@wattanx/hooper-vue3'
import '@wattanx/hooper-vue3/css'

import type { PickupItems } from '@/types/appTypes'

type Props = { items: PickupItems[] }
const props = withDefaults(defineProps<Props>(), {})

const app = useNuxtApp()

const hooperReport = {
  itemsToShow: 6,
  centerMode: false,
  infiniteScroll: true,
  wheelControl: false,
  breakpoints: {
    0: {
      itemsToShow: 1.3,
      centerMode: true,
    },
    751: { itemsToShow: 6 },
  },
}

const reportSlider = ref(null)

const onClickReportPrev = () => {
  reportSlider.value.restartTimer()
  reportSlider.value.slidePrev()
}

const onClickReportNext = () => {
  reportSlider.value.restartTimer()
  reportSlider.value.slideNext()
}
</script>

<template>
  <div class="p-topReportSlider">
    <div class="p-topReportSlider_inner">
      <hooper
        key="report"
        ref="reportSlider"
        v-bind:settings="hooperReport"
        auto-play
        v-bind:play-speed="4000"
        v-bind:transition="600"
      >
        <slide
          v-for="(item, index) in items"
          v-bind:key="index"
          v-bind:index="index"
        >
          <div class="c-reportTopItem">
            <div class="c-reportTopItem_imageWrap">
              <div
                class="c-reportTopItem_imagePC"
              >
                <nuxt-link
                  v-bind:to="{
                    name: 'article-slug',
                    params: {
                      article: item.article_type_slug,
                      slug: item.article_slug,
                    },
                  }"
                >
                  <CommonImage
                    v-bind:src="item.imageUrl"
                    error-src="/images/noimage.svg"
                    v-bind:alt="item.title"
                    object-fit="cover"
                    aspect-ratio="452/339"
                    is-hover-effect
                  />
                </nuxt-link>
              </div>
              <div
                class="c-reportTopItem_imageSP"
              >
                <CommonImage
                  v-bind:src="item.imageUrl"
                  error-src="/images/noimage.svg"
                  v-bind:alt="item.title"
                  object-fit="cover"
                  aspect-ratio="452/339"
                  is-hover-effect
                />
              </div>
              <nuxt-link
                v-bind:to="{
                  name: 'article-slug',
                  params: {
                    article: item.article_type_slug,
                    slug: item.article_slug,
                  },
                }"
              >
                <template
                  v-if="item.article_type_slug === 'special'"
                >
                  <div
                    class="c-reportTopItem_chip is-special"
                  >
                    特集
                  </div>
                </template>
                <template
                  v-else-if="item.article_type_slug === 'news'"
                >
                  <div
                    class="c-reportTopItem_chip is-news"
                  >
                    ニュース
                  </div>
                </template>
                <template
                  v-else-if="item.is_im_report === '1'"
                >
                  <div
                    class="c-reportTopItem_chip is-imReport"
                  >
                    IMレポート
                  </div>
                </template>
                <template
                  v-else
                >
                  <div
                    class="c-reportTopItem_chip"
                  >
                    読者レポート
                  </div>
                </template>
              </nuxt-link>
            </div>

            <!--
            <div class="c-reportTopItem_place">
              〇〇県
            </div>
            -->
            <nuxt-link
              class="c-reportTopItem_title c-clampLines is-clampline2"
              v-bind:to="{
                name: 'article-slug',
                params: {
                  article: item.article_type_slug,
                  slug: item.article_slug,
                },
              }"
            >
              {{ item.title }}
            </nuxt-link>
          </div>
        </slide>
      </hooper>
    </div>
    <v-btn
      class="p-topReportSlider_prevButton"
      v-on:click="onClickReportPrev"
    >
      <v-icon size="large">
        mdi-chevron-left
      </v-icon>
    </v-btn>
    <v-btn
      class="p-topReportSlider_nextButton"
      v-on:click="onClickReportNext"
    >
      <v-icon size="large">
        mdi-chevron-right
      </v-icon>
    </v-btn>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive" as responsive;

:deep(.hooper) {
  height: auto;
}

.p-topReportSlider {
  position: relative;
  width: 100%;
  height: 400px;

  @include responsive.mq_down(sp) {
    left: 0;
    width: 100%;
    margin-left: 0;
  }

  @media screen and (width <= 600px) {
    height: 450px;
  }

  @media screen and (width <= 500px) {
    height: 380px;
  }

  @media screen and (width <= 440px) {
    height: 350px;
  }

  @media screen and (width <= 400px) {
    height: 330px;
  }

  @include responsive.mq_down(small) {
    height: 320px;
  }

  &_inner {
    position: absolute;
    left: 50%;
    width: 2952px;
    height: 464px;
    margin-left: -1476px;

    @include responsive.mq_down(sp) {
      left: 0;
      width: 100%;
      margin-left: 0;
    }
  }

  &_prevButton {
    position: absolute;
    top: 50%;
    left: 1px;
    height: 40px !important;
    margin-top: -20px;
    padding: 0 10px !important;
    border-radius: 0;

    @include responsive.mq_down(sp) {
      display: none;
    }
  }

  &_nextButton {
    position: absolute;
    top: 50%;
    right: 1px;
    height: 40px !important;
    margin-top: -20px;
    padding: 0 10px !important;
    border-radius: 0;

    @include responsive.mq_down(sp) {
      display: none;
    }
  }
}

.c-reportTopItem {
  position: relative;
  width: 492px;
  padding-right: 20px;
  padding-left: 20px;
  color: var(--g_color_text_white);
  font-family: var(--g_font_family_NotoSansCJKjp);

  @include responsive.mq_down(sp) {
    width: 100%;
    padding-right: 10px;
    padding-left: 10px;
  }

  &_imagePC {
    @include responsive.mq_down(sp) {
      display: none;
    }
  }

  &_imageSP {
    @include responsive.mq_up(pc) {
      display: none;
    }
  }

  &_imageWrap {
    position: relative;
  }

  &_chip {
    position: absolute;
    bottom: 15px;
    left: 0;
    align-items: center;
    justify-content: center;
    padding: 5px 25px 10px;

    // opacity: 0.7;
    // background-color: var(--color_report_author_bg_user);
    background: rgb(0, 148, 152, 0.7);
    color: var(--g_color_text_white);
    font-size: 16px;
    font-weight: normal;
    line-height: 30px;
    text-align: center;

    &::after {
      content: "";
      display: block;
      position: relative;
      height: 2px;
      background-color: #fff;
    }

    @include responsive.mq_down(sp) {
      bottom: 10px;
      padding: 0 18px 5px;
      font-size: 13px;
      line-height: 25px;
    }

    &.is-special {
      // background-color: var(--color_special_author_bg);
      background: rgb(215, 12, 122, 0.7);
    }

    &.is-news {
      background-color: var(--color_news_author_bg);
    }

    &.is-imReport {
      // background-color: var(--color_report_author_bg_im);
      background: rgb(1, 77, 163, 0.7);
    }
  }

  &_place {
    margin-top: 16px;
    opacity: 0.5;
    font-size: 14px;
    font-weight: 600;
  }

  &_title {
    min-height: 50px;
    margin-top: 7px;
    color: var(--g_color_text_white);
    font-size: 16px;
    font-weight: 600;
  }

  &_info {
    display: flex;
    flex-wrap: wrap;
    margin-top: 10px;
    font-size: 14px;

    &_term {
      margin-right: 40px;
      font-weight: 600;
    }

    &_copy {
      font-weight: 400;
    }
  }
}
</style>
